import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ky from "ky";
import Logo from "../components/ui/Logo.jsx";
import ParticleBackground from "../components/ui/ParticleBackground.jsx";
import { useAuthStore } from "../lib/store.js";

const schema = z.object({
  email:    z.string().email("Invalid email"),
  password: z.string().min(1, "Password required"),
});

export default function Login() {
  const navigate  = useNavigate();
  const setUser   = useAuthStore((s) => s.setUser);
  const [error, setError] = useState(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });

  const [rememberMe, setRememberMe] = useState(false);

  async function onSubmit({ email, password }) {
    setError(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content ?? "";
      const data = await ky.post("/users/sign_in", {
        json: { user: { email, password, remember_me: rememberMe ? "1" : "0" } },
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        credentials: "include",
      }).json();
      setUser(data.user);
      navigate("/");
    } catch (e) {
      setError("Invalid email or password");
    }
  }

  return (
    <div className="login-bg scanlines h-full flex items-center justify-center relative overflow-hidden">
      <ParticleBackground />

      {/* Ambient glow orbs */}
      <div
        style={{
          position: "absolute", top: "15%", left: "10%",
          width: 400, height: 400,
          background: "radial-gradient(circle, rgba(0,245,255,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
          borderRadius: "50%",
        }}
      />
      <div
        style={{
          position: "absolute", bottom: "20%", right: "15%",
          width: 300, height: 300,
          background: "radial-gradient(circle, rgba(191,0,255,0.05) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Login card */}
      <div
        className="relative z-10 w-full max-w-md"
        style={{ padding: "0 24px" }}
      >
        <div
          className="card p-8 login-card-glow"
          style={{
            border: "1px solid rgba(0,245,255,0.35)",
            boxShadow: "0 0 20px rgba(0,245,255,0.12), 0 0 60px rgba(0,245,255,0.06), inset 0 0 40px rgba(0,0,0,0.4)",
          }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo size={72} />
          </div>

          {/* Tagline */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label
                className="block mb-1 data text-xs uppercase"
                style={{ color: "var(--color-muted)", letterSpacing: "0.1em" }}
              >
                Operator ID (Email)
              </label>
              <input
                {...register("email")}
                type="email"
                className="input"
                placeholder="operator@example.com"
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-xs data" style={{ color: "var(--color-red)" }}>
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block mb-1 data text-xs uppercase"
                style={{ color: "var(--color-muted)", letterSpacing: "0.1em" }}
              >
                Access Key
              </label>
              <input
                {...register("password")}
                type="password"
                className="input"
                placeholder="••••••••••••"
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="mt-1 text-xs data" style={{ color: "var(--color-red)" }}>
                  {errors.password.message}
                </p>
              )}
            </div>

            <label
              className="flex items-center gap-2 cursor-pointer select-none"
              style={{ marginTop: -4 }}
            >
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: "var(--color-blue)", width: 14, height: 14, cursor: "pointer" }}
              />
              <span className="data text-xs uppercase" style={{ color: "var(--color-muted)", letterSpacing: "0.1em" }}>
                Keep me signed in
              </span>
            </label>

            {error && (
              <div
                className="p-3 rounded data text-sm"
                style={{
                  background: "rgba(255,51,85,0.1)",
                  border: "1px solid rgba(255,51,85,0.4)",
                  color: "var(--color-red)",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-cyan w-full justify-center"
              style={{ fontSize: 14, padding: "12px 24px", marginTop: 8 }}
            >
              {isSubmitting ? "AUTHENTICATING..." : "ACCESS SYSTEM"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <a
              href="/users/password/new"
              className="data text-xs"
              style={{ color: "var(--color-muted)", letterSpacing: "0.08em", textDecoration: "none" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "var(--color-blue)"}
              onMouseLeave={(e) => e.currentTarget.style.color = "var(--color-muted)"}
            >
              Forgot password?
            </a>
          </div>

          {/* Decorative bottom line */}
          <div
            className="mt-8 pt-4 border-t text-center data text-xs"
            style={{ borderColor: "var(--color-border)", color: "var(--color-muted)", letterSpacing: "0.15em" }}
          >
            RAVELOCATOR v1.0
          </div>
        </div>
      </div>
    </div>
  );
}
